import { lazy, Suspense } from 'react';
import { Link, NavLink } from 'react-router-dom';
import classNames from 'classnames';
import { useStoreState } from 'easy-peasy';
import { useAuthorisation } from 'Components/Authorised';
import {
  usePartners,
  useCustomers,
  useWarehouses,
  useConsolidation,
  useDeliveries,
  CustomerRequestController,
} from '~/store/models';
import { useParticipantsAcl } from '~/hooks/useParticipants';
import { useCounterparty } from '~/hooks/useCounterparty';
import { getEnv } from '~/utils';
// Lazy-loaded: the navbar tender count only renders on solution pages (when
// tender access exists), so its tender-service chain stays out of the entry
// chunk and loads on demand.
const TenderCount = lazy(() =>
  import('Pages/Tenders/TenderCount').then((m) => ({ default: m.TenderCount }))
);
import { FormattedMessage, useIntl } from 'react-intl';
import Icon from 'Components/Icon';
import nuiHorizontal from 'Assets/img/nui-horizontal.svg';
import { useMarketOrders } from 'Pages/Market/hooks/useMarketOrders';
import { useRoute } from '~/router/solutionUrl';
import { usePermissions } from '~/hooks/usePermissions';
import { ADMIN_COMPANY_ID } from '~/constants';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import EnvBadge from './EnvBadge';

// Static message definitions for extraction tools
const NAV_MESSAGES = {
  marketplace: {
    label: (
      <FormattedMessage
        id="navbar-tab-marketplace"
        defaultMessage="Marketplace"
      />
    ),
    descriptor: { id: 'navbar-tab-marketplace', defaultMessage: 'Marketplace' },
  },
  tenders: {
    label: (
      <FormattedMessage id="navbar-tab-tenders" defaultMessage="Tenders" />
    ),
    descriptor: { id: 'navbar-tab-tenders', defaultMessage: 'Tenders' },
  },
  lpOverview: {
    label: (
      <FormattedMessage
        id="navbar-tab-lp-overview"
        defaultMessage="LP Overview"
      />
    ),
    descriptor: { id: 'navbar-tab-lp-overview', defaultMessage: 'LP Overview' },
  },
  orders: {
    label: <FormattedMessage id="navbar-tab-orders" defaultMessage="Orders" />,
    descriptor: { id: 'navbar-tab-orders', defaultMessage: 'Orders' },
  },
  trades: {
    label: <FormattedMessage id="navbar-tab-trades" defaultMessage="Trades" />,
    descriptor: { id: 'navbar-tab-trades', defaultMessage: 'Trades' },
  },
  participants: {
    label: (
      <FormattedMessage
        id="navbar-tab-participants"
        defaultMessage="Participants"
      />
    ),
    descriptor: {
      id: 'navbar-tab-participants',
      defaultMessage: 'Participants',
    },
  },
  consolidation: {
    label: (
      <FormattedMessage
        id="navbar-tab-consolidation"
        defaultMessage="Consolidation"
      />
    ),
    descriptor: {
      id: 'navbar-tab-consolidation',
      defaultMessage: 'Consolidation',
    },
  },
  deliveries: {
    label: (
      <FormattedMessage
        id="navbar-tab-deliveries"
        defaultMessage="Deliveries"
      />
    ),
    descriptor: { id: 'navbar-tab-deliveries', defaultMessage: 'Deliveries' },
  },
  routes: {
    label: <FormattedMessage id="navbar-tab-routes" defaultMessage="Routes" />,
    descriptor: { id: 'navbar-tab-routes', defaultMessage: 'Routes' },
  },
  partners: {
    label: (
      <FormattedMessage id="navbar-tab-partners" defaultMessage="Partners" />
    ),
    descriptor: { id: 'navbar-tab-partners', defaultMessage: 'Partners' },
  },
  products: {
    label: (
      <FormattedMessage id="navbar-tab-products" defaultMessage="Products" />
    ),
    descriptor: { id: 'navbar-tab-products', defaultMessage: 'Products' },
  },
  locations: {
    label: (
      <FormattedMessage id="navbar-tab-locations" defaultMessage="Locations" />
    ),
    descriptor: { id: 'navbar-tab-locations', defaultMessage: 'Locations' },
  },
  customers: {
    label: (
      <FormattedMessage id="navbar-tab-customers" defaultMessage="Customers" />
    ),
    descriptor: { id: 'navbar-tab-customers', defaultMessage: 'Customers' },
  },
  suppliers: {
    label: (
      <FormattedMessage id="navbar-tab-suppliers" defaultMessage="Suppliers" />
    ),
    descriptor: { id: 'navbar-tab-suppliers', defaultMessage: 'Suppliers' },
  },
  customerRequests: {
    label: (
      <FormattedMessage
        id="navbar-tab-customer-request"
        defaultMessage="Customer requests"
      />
    ),
    descriptor: {
      id: 'navbar-tab-customer-request',
      defaultMessage: 'Customer requests',
    },
  },
  reports: {
    label: (
      <FormattedMessage id="navbar-tab-reports" defaultMessage="Reports" />
    ),
    descriptor: { id: 'navbar-tab-reports', defaultMessage: 'Reports' },
  },
  users: {
    label: <FormattedMessage id="navbar-tab-users" defaultMessage="Users" />,
    descriptor: { id: 'navbar-tab-users', defaultMessage: 'Users' },
  },
  viewPages: {
    descriptor: { id: 'navbar-tab-view-pages', defaultMessage: 'View pages' },
  },
} as const;

type NavMessageKey = keyof typeof NAV_MESSAGES;

interface NavItemConfig {
  to: string;
  messageKey: NavMessageKey;
  isActive?: (match: unknown, location: { pathname: string }) => boolean;
}

interface SolutionDivision {
  createliquidity?: boolean;
}

const Nav = () => {
  const { formatMessage } = useIntl();
  const { can } = useAuthorisation();
  const domain = getEnv(import.meta.env.VITE_APP_OLD_DOMAIN);
  const user = useStoreState((state) => state.auth.userModel);
  const isadmin = user?.company?.id === ADMIN_COMPANY_ID;
  const isSolutionOwner = useStoreState((state) => state.auth.isSolutionOwner);
  const isdao = useStoreState((state) => state.auth.isdao);
  const solution = useStoreState((state) => state.auth.solution);
  const { marketplace, tender } = useStoreState(
    (state) => state.auth.solutionSettings
  );
  const route = useRoute();

  const participants = useParticipantsAcl();
  const customers = useCustomers();
  const counterparty = useCounterparty();
  const warehouses = useWarehouses();
  const partners = usePartners();
  const consolidation = useConsolidation();
  const deliveries = useDeliveries();

  const { data: marketData } = useMarketOrders();
  const totalOffers =
    marketData?.orders?.filter(
      (x) => (x as unknown as { type: string }).type === 'offer'
    ).length || 0;
  const totalBids =
    marketData?.orders?.filter(
      (x) => (x as unknown as { type: string }).type === 'bid'
    ).length || 0;

  const routeTypes = ['port', 'zone'];
  const hasRoutes =
    isSolutionOwner &&
    [...solution.freight_types, solution.freight].some((type) =>
      routeTypes.includes(type)
    );

  const solutionDivisions = useStoreState(
    (state) => state.auth.solutionDivisions
  );
  const canCreateLiquidity = solutionDivisions.some(
    (s: SolutionDivision) => s.createliquidity
  );

  const navItems: (NavItemConfig & {
    visible?: boolean;
    subContent?: React.ReactNode;
  })[] = [
    {
      to: route('/'),
      messageKey: 'marketplace',
      visible: !!marketplace,
      isActive: (_match, location) =>
        [route('/'), route('/overview')].includes(location.pathname),
      subContent:
        totalOffers > 0 || totalBids > 0
          ? [
              totalOffers &&
                formatMessage(
                  {
                    id: 'navbar-tab-marketplace-sub-offers',
                    defaultMessage:
                      '{count, plural, =1 {1 offer} other {# offers}}',
                  },
                  { count: totalOffers }
                ),
              totalBids &&
                formatMessage(
                  {
                    id: 'navbar-tab-marketplace-sub-bids',
                    defaultMessage:
                      '{count, plural, =1 {1 bid} other {# bids}}',
                  },
                  { count: totalBids }
                ),
            ]
              .filter(Boolean)
              .join(' | ')
          : undefined,
    },
    {
      to: route('/tenders'),
      messageKey: 'tenders',
      visible: !!tender,
      subContent: (
        <Suspense fallback={null}>
          <TenderCount />
        </Suspense>
      ),
    },
    {
      to: route('/lp-overview'),
      messageKey: 'lpOverview',
      visible: canCreateLiquidity,
    },
    {
      to: route('/orders'),
      messageKey: 'orders',
    },
    {
      to: route('/trades'),
      messageKey: 'trades',
    },
    {
      to: route('/participants'),
      messageKey: 'participants',
      visible: can('view', participants),
    },
    {
      to: route('/consolidation'),
      messageKey: 'consolidation',
      visible: can('view', consolidation),
    },
    {
      to: route('/deliveries'),
      messageKey: 'deliveries',
      visible: can('view', deliveries),
    },
    {
      to: route('/routes'),
      messageKey: 'routes',
      visible: hasRoutes,
    },
    {
      to: route('/partners'),
      messageKey: 'partners',
      visible: can('view', partners),
    },
    {
      to: route('/products'),
      messageKey: 'products',
    },
    {
      to: route('/locations'),
      messageKey: 'locations',
      visible: can('view', warehouses),
    },
    {
      to: route('/customers'),
      messageKey: counterparty === 'supplier' ? 'suppliers' : 'customers',
      visible: can('view', customers),
    },
    {
      to: route('/customer-requests'),
      messageKey: 'customerRequests',
      visible: can('view', CustomerRequestController),
    },
    {
      to: route('/reports'),
      messageKey: 'reports',
      visible: isSolutionOwner || isdao,
    },
    {
      to: route('/users'),
      messageKey: 'users',
    },
  ];

  const visibleItems = navItems.filter((item) => item.visible !== false);
  const adminLabel = formatMessage({
    id: 'navbar-tab-admin',
    defaultMessage: 'Admin',
  });
  const desktopVisibleCount = 4;
  const tabletVisibleCount = 2;

  const renderDropdownNavItem = (item: (typeof visibleItems)[number]) => {
    const msg = NAV_MESSAGES[item.messageKey];
    return (
      <DropdownMenuItem key={item.to} asChild>
        <Link to={item.to} title={formatMessage(msg.descriptor)}>
          {msg.label}
        </Link>
      </DropdownMenuItem>
    );
  };

  return (
    <nav className="tw tw:sm:self-start tw:relative tw:sm:top-[1.04rem]">
      <ul className="tw:flex tw:gap-5 tw:text-[1.05rem] tw:font-medium">
        {/* Desktop: show first 4 items */}
        {visibleItems.slice(0, desktopVisibleCount).map((item, index) => {
          const msg = NAV_MESSAGES[item.messageKey];
          return (
            <li
              key={item.to}
              className={classNames({
                'tw:hidden tw:lg:block': index >= tabletVisibleCount,
                'tw:hidden tw:md:block': index < tabletVisibleCount,
              })}
            >
              <NavLink
                to={item.to}
                className="tw:text-black tw:hover:text-gray-500 tw:transition-colors"
                activeClassName="tw:!text-[var(--brand-color)]"
                title={formatMessage(msg.descriptor)}
                isActive={item.isActive}
              >
                {msg.label}
              </NavLink>
              {item.subContent && (
                <div
                  data-testid="nav-subcontent"
                  className="tw:text-[0.7rem] tw:mt-0.5 tw:font-light tw:text-gray-500 tw:leading-1"
                >
                  {item.subContent}
                </div>
              )}
            </li>
          );
        })}

        {/* Overflow menu */}
        <li>
          <div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="tw:inline-flex tw:items-center tw:border-none tw:bg-transparent tw:text-black tw:hover:text-gray-500 tw:transition-colors"
                  aria-label={formatMessage(NAV_MESSAGES.viewPages.descriptor)}
                >
                  <FormattedMessage
                    id="nav-overflow-more"
                    defaultMessage="More"
                  />
                  <Icon name="arrow_drop_down" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {[
                  { className: 'tw:md:hidden', sliceFrom: 0 },
                  {
                    className: 'tw:hidden tw:md:block tw:lg:hidden',
                    sliceFrom: tabletVisibleCount,
                  },
                  {
                    className: 'tw:hidden tw:lg:block',
                    sliceFrom: desktopVisibleCount,
                  },
                ].map(({ className, sliceFrom }) => (
                  <div key={className} className={className}>
                    {visibleItems
                      .slice(sliceFrom)
                      .map((item) => renderDropdownNavItem(item))}
                  </div>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </li>
        {isadmin && (
          <li>
            <a
              href={`${domain}/signin`}
              target="_blank"
              rel="noopener noreferrer"
              title={adminLabel}
              className="tw:h-6 tw:px-2 tw:flex tw:items-center tw:justify-center tw:rounded-full tw:hover:bg-gray-100 tw:transition-colors tw:border tw:text-xs tw:uppercase"
            >
              {adminLabel}
            </a>
          </li>
        )}
      </ul>
    </nav>
  );
};

const TradeButtons = () => {
  const {
    canBuy,
    canSell,
    canCreateTender,
    hasRestrictedAccess,
    isMarketplaceEnabled,
  } = usePermissions();
  const route = useRoute();

  const optionCount = [canBuy, canSell, canCreateTender].filter(Boolean).length;

  if (!isMarketplaceEnabled || hasRestrictedAccess || optionCount === 0)
    return <></>;

  const buttonClass =
    'tw:inline-flex tw:items-center tw:gap-1 tw:pl-2 tw:pr-4 tw:py-1.5 tw:font-medium tw:rounded-full tw:bg-[var(--brand-color)] tw:text-white tw:hover:text-white tw:hover:bg-[var(--brand-color)]/80 tw:transition-colors tw:no-underline';

  // Single option: show direct button
  if (optionCount === 1) {
    const link = canBuy
      ? route('/orders/bid')
      : canSell
        ? route('/orders/offer')
        : route('/tenders/new');
    return (
      <Link to={link} className={buttonClass}>
        <Icon name="add" />
        <FormattedMessage id="navbar-button-create" defaultMessage="Create" />
      </Link>
    );
  }

  // Multiple options: show dropdown
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className={buttonClass}>
          <Icon name="add" />
          <FormattedMessage id="navbar-button-create" defaultMessage="Create" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {canBuy && (
          <DropdownMenuItem asChild>
            <Link to={route('/orders/bid')} className="tw:w-full tw:p-2.5!">
              <div>
                <strong className="tw:text-[16px] tw:font-bold!">
                  <FormattedMessage
                    id="navbar-dropdown-newbid"
                    defaultMessage="New bid"
                  />
                </strong>
                <div>
                  <FormattedMessage
                    id="navbar-dropdown-newbid-description"
                    defaultMessage="Request to buy products"
                  />
                </div>
              </div>
            </Link>
          </DropdownMenuItem>
        )}
        {canSell && (
          <DropdownMenuItem asChild>
            <Link to={route('/orders/offer')} className="tw:w-full tw:p-2.5!">
              <div>
                <strong className="tw:text-[16px] tw:font-bold!">
                  <FormattedMessage
                    id="navbar-dropdown-newoffer"
                    defaultMessage="New offer"
                  />
                </strong>
                <div>
                  <FormattedMessage
                    id="navbar-dropdown-newoffer-description"
                    defaultMessage="List products for sale"
                  />
                </div>
              </div>
            </Link>
          </DropdownMenuItem>
        )}
        {canCreateTender && (
          <DropdownMenuItem asChild>
            <Link to={route('/tenders/new')} className="tw:w-full tw:p-2.5!">
              <div>
                <strong className="tw:text-[16px] tw:font-bold!">
                  <FormattedMessage
                    id="navbar-dropdown-newtender"
                    defaultMessage="New tender"
                  />
                </strong>
                <div>
                  <FormattedMessage
                    id="navbar-dropdown-newtender-description"
                    defaultMessage="Schedule a live auction"
                  />
                </div>
              </div>
            </Link>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface HeaderProps {
  home?: boolean;
}

const Header = ({ home }: HeaderProps) => {
  const solution = useStoreState((state) => state.auth.solution);
  const { formatMessage } = useIntl();
  const route = useRoute();

  const logoAlt = formatMessage({
    id: 'header-logo-alt',
    defaultMessage: 'Home | Nui Markets',
  });
  const defaultLogo = nuiHorizontal;

  if (home) {
    return (
      <div className="tw tw:px-4 tw:lg:px-6 tw:py-3 tw:sm:py-4">
        {/* shim for mobile to inject padding above */}
        <div className="tw:invisible">
          <EnvBadge />
        </div>
        <img
          className="tw:h-7 tw:sm:h-10"
          src={defaultLogo}
          alt={logoAlt}
          fetchPriority="high"
        />
      </div>
    );
  }

  const mobileLogo = solution.logo || defaultLogo;
  const desktopLogo = solution.logo_wide || mobileLogo;
  const dashboards = solution?.dashboards || [];

  return (
    <header
      role="navigation"
      className="tw tw:px-4 tw:py-2 tw:sm:py-3 tw:border-b-6 tw:border-[var(--brand-color)] tw:bg-white"
    >
      <div className="tw:flex tw:items-center">
        <Link
          to={route('/')}
          className="tw:mr-5 tw:h-7 tw:sm:h-14 tw:inline-block"
        >
          {solution.logo_wide ? (
            <>
              <img
                className="tw:lg:hidden tw:h-full"
                src={mobileLogo}
                alt={logoAlt}
              />
              <img
                className="tw:hidden tw:lg:block tw:h-full"
                src={desktopLogo}
                alt={logoAlt}
              />
            </>
          ) : (
            <img className="tw:h-full" src={mobileLogo} alt={logoAlt} />
          )}
        </Link>
        <div className="tw:ml-auto tw:sm:ml-0 tw:mr-3 tw:sm:mr-4 tw:mt-px">
          <TradeButtons />
        </div>
        <Nav />
        {dashboards.length > 0 && (
          <div className="tw:ml-3 tw:sm:ml-auto">
            <Tooltip>
              <TooltipTrigger asChild>
                <NavLink
                  to={route('/dashboard')}
                  className="tw:text-black tw:flex tw:items-center tw:no-underline"
                  activeClassName="tw:!text-[var(--brand-color)]"
                >
                  <Icon name="finance" />
                </NavLink>
              </TooltipTrigger>
              <TooltipContent>
                <FormattedMessage
                  id="navbar-tab-dashboard"
                  defaultMessage="Dashboards are configurable to aid your trade decision making process."
                />
              </TooltipContent>
            </Tooltip>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
